import { useState, useRef, useEffect, useCallback } from 'react';
import Hls from 'hls.js';
import { Stream as CloudflareStream } from '@cloudflare/stream-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useDurableObjectChannel } from '@/hooks/use-durable-object-channel';
import { streamHealthMonitor } from '@/services/stream-health';
import type { Stream } from '@/lib/stores/streams/types';
import { api } from '@/lib/api/client';
import { StreamProgressBar } from './StreamProgressBar';
import { StreamControls } from './StreamControls';
import { StreamOverlays } from './StreamOverlays';
import { CaptionOverlay } from './CaptionOverlay';
import { useAuthStore } from '@/lib/stores/auth';
import { resolveBundledMedia } from '@/lib/media-fallback';

interface StreamPlayerProps {
  stream: Stream | null;
  autoPlay?: boolean;
  className?: string;
}

interface QualityLevel {
  height: number;
  bitrate: number;
  label: string;
}

interface PlaybackResponse {
  playbackUrl?: string | null;
  url?: string;
  status?: 'ready' | 'pending' | 'processing' | 'unavailable';
  reason?: string;
  streamStatus?: string;
  recordingStatus?: string;
}

export function StreamPlayer({ stream, autoPlay = false }: StreamPlayerProps) {
  const { user } = useAuthStore();
  // Early return if stream is null or undefined
  if (!stream || !stream.id) {
    return (
      <div className="w-full aspect-video bg-gray-900 rounded-lg flex items-center justify-center">
        <div className="text-white text-center">
          <p className="text-lg font-medium">Stream not available</p>
          <p className="text-sm text-gray-400">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile({ containerRef, breakpoint: 760 });
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const cloudflareStreamRef = useRef<any>(null); // Cloudflare Stream player API ref

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isDubAudioMutingOriginal, setIsDubAudioMutingOriginal] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [showInfoSheet, setShowInfoSheet] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorType, setErrorType] = useState<'network' | 'playback' | 'auth' | 'unknown'>('unknown');
  const [retryCount, setRetryCount] = useState(0);
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [qualityLevels, setQualityLevels] = useState<QualityLevel[]>([]);
  const [currentQuality, setCurrentQuality] = useState<number>(-1); // -1 = auto
  const [useIframePlayer, setUseIframePlayer] = useState(false); // Fallback to Cloudflare iframe player
  const [useCloudflarePlayer, setUseCloudflarePlayer] = useState(true); // Use Cloudflare Stream React component

  // Live buffer tracking state
  const [liveBufferDuration, setLiveBufferDuration] = useState(0);
  const [seekableStart, setSeekableStart] = useState(0);
  const [seekableEnd, setSeekableEnd] = useState(0);

  // Computed: Check if this is a live stream (not a recording)
  const isLiveStream = stream.status === 'live';
  const isRecording = stream.status === 'ended' && !!stream.recordingId;
  const isAudioMuted = isMuted || isDubAudioMutingOriginal;

  // Helper to detect Cloudflare Stream URLs
  const isCloudflareStreamUrl = (url: string): boolean => {
    return url.includes('cloudflarestream.com') || url.includes('videodelivery.net');
  };

  // Helper to extract video UID from Cloudflare Stream URL
  const getVideoUidFromUrl = (url: string): string | null => {
    // Format: https://customer-xxx.cloudflarestream.com/{videoUid}/manifest/video.m3u8
    const match = url.match(/cloudflarestream\.com\/([^/]+)\/manifest/);
    return match ? match[1] : null;
  };

  // Helper to get iframe URL from manifest URL
  const getIframeUrlFromManifest = (manifestUrl: string): string | null => {
    // Extract video UID from manifest URL
    // Format: https://customer-xxx.cloudflarestream.com/{videoUid}/manifest/video.m3u8
    const match = manifestUrl.match(/cloudflarestream\.com\/([^/]+)\/manifest/);
    if (match && match[1]) {
      const videoUid = match[1];
      const subdomain = manifestUrl.match(/https:\/\/([^/]+)/)?.[1] || 'customer.cloudflarestream.com';
      return `https://${subdomain}/${videoUid}/iframe`;
    }
    return null;
  };

  // Get seekable range from video element
  const getSeekableRange = (video: HTMLVideoElement): { start: number; end: number } => {
    if (video.seekable.length === 0) {
      return { start: 0, end: 0 };
    }
    return {
      start: video.seekable.start(0),
      end: video.seekable.end(video.seekable.length - 1)
    };
  };

  // No sanitization - duration is either valid or unknown (0)
  // We never accept garbage values

  // Real-time stream health and analytics via Durable Objects
  const [streamHealth, setStreamHealth] = useState<any>(null);
  const [streamAnalytics, setStreamAnalytics] = useState<any>(null);

  const handleAnalyticsMessage = useCallback((message: any) => {
    if (message.type === 'stream_health_update' && message.streamId === stream.id) {
      setStreamHealth(message.healthStatus || message.healthData);
    } else if (message.type === 'stream_analytics_update' && message.streamId === stream.id) {
      setStreamAnalytics(message.analytics);
    }
  }, [stream.id]);

  const { isConnected: analyticsConnected } = useDurableObjectChannel({
    channel: stream.status === 'live' ? `stream:analytics:${stream.id}` : '',
    enabled: stream.status === 'live',
    onMessage: handleAnalyticsMessage,
  });

  // Stream health monitoring
  useEffect(() => {
    if (stream.status !== 'live') return;

    const updateHealthMetrics = () => {
      if (!videoRef.current) return;

      const video = videoRef.current;
      const hls = hlsRef.current;

      // Get HLS stats if available
      let bitrate = 0;
      let resolution = 'unknown';
      let framerate = 0;

      if (hls && hls.levels && hls.currentLevel >= 0) {
        const level = hls.levels[hls.currentLevel];
        if (level) {
          bitrate = level.bitrate;
          resolution = level.height ? `${level.height}p` : 'unknown';
          // Framerate estimation (rough)
          framerate = level.frameRate || 30;
        }
      }

      // Calculate buffer health
      const buffered = video.buffered;
      const currentTime = video.currentTime;
      const duration = video.duration || 1;

      let bufferHealth = 100;
      if (buffered.length > 0) {
        const bufferedEnd = buffered.end(buffered.length - 1);
        const timeToBufferEnd = bufferedEnd - currentTime;
        bufferHealth = Math.min(100, (timeToBufferEnd / 10) * 100); // 10 seconds buffer target
      }

      streamHealthMonitor.updateMetrics(stream.id, {
        bitrate,
        resolution,
        framerate,
        bufferHealth,
        errorCount: error ? 1 : 0
      });
    };

    // Update metrics every 5 seconds
    const interval = setInterval(() => {
      updateHealthMetrics();
    }, 5000);

    // Periodic access re-validation for mid-session revocation detection (every 60 seconds)
    const accessCheckInterval = setInterval(async () => {
      if (!stream.id || !user) return;

      try {
        const accessResponse = await api.get(`/api/streams/${stream.id}/access/check`);
        if (!accessResponse.success || !(accessResponse.data as any)?.has_access) {
          console.warn(`[StreamPlayer] Access revoked for stream ${stream.id} - mid-session revocation detected`);
          setError('Access to this content has been revoked');
          setErrorType('auth');
          setPlaybackUrl(null);
        }
      } catch (err) {
        console.warn('[StreamPlayer] Access check failed:', err);
      }
    }, 60000);

    // Initial update
    updateHealthMetrics();

    return () => {
      clearInterval(interval);
      clearInterval(accessCheckInterval);
    };
  }, [stream.id, stream.status, error, retryCount, user]);

  // Monitor for stream errors and trigger recovery
  useEffect(() => {
    if (!error || stream.status !== 'live') return;

    // Record the error
    streamHealthMonitor.recordError(stream.id, {
      type: 'playback_error',
      message: error
    });

    // Attempt automatic recovery after a delay
    const recoveryTimeout = setTimeout(async () => {
      const recovered = await streamHealthMonitor.attemptRecovery(stream.id);
      if (recovered) {
        // Clear error and retry loading
        setError(null);
        setIsRecovering(true);
        // The HLS.js error handler will attempt to recover
      }
    }, 3000);

    return () => clearTimeout(recoveryTimeout);
  }, [error, stream.id, stream.status]);

  // Check access first - default to true if undefined to avoid blocking during dev if API mismatch
  const hasAccess = stream.has_access !== false;

  // State for pending stream (waiting for RTMP to start)
  const [isPending, setIsPending] = useState(false);
  const [pendingReason, setPendingReason] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingReason, setProcessingReason] = useState<string | null>(null);
  // Removed polling - using WebSocket subscriptions instead

  // Playback response type
  interface PlaybackResponse {
    playbackUrl?: string | null;
    url?: string;
    status?: 'ready' | 'pending' | 'processing';
    reason?: string;
    streamStatus?: string;
  }

  // Fetch signed playback URL from API - extracted to useCallback so it can be used in multiple places
  const fetchPlaybackUrl = useCallback(async (): Promise<boolean> => {
    if (!stream.id) return false;

    try {
      setError(null);

      const response = await api.get<PlaybackResponse>(`/api/streams/${stream.id}/playback`);

      if (response.success && response.data) {
        // Check if stream is pending (waiting for RTMP input - live streams)
        if (response.data.status === 'pending') {
          console.log(`Stream ${stream.id} is pending: ${response.data.reason}`);
          setIsPending(true);
          setIsProcessing(false);
          setPendingReason(response.data.reason || 'Waiting for stream to start');
          setIsLoading(false);
          return false; // Not ready yet
        }

        // Check if recording is processing (ended streams)
        if (response.data.status === 'processing') {
          console.log(`Stream ${stream.id} recording is processing: ${response.data.reason}`);
          setIsProcessing(true);
          setIsPending(false);
          setProcessingReason(response.data.reason || 'Recording is being processed');
          setIsLoading(false);
          return false; // Not ready yet
        }

        // Stream is ready - get the URL
        const url = response.data.playbackUrl || response.data.url || '';
        // Validate URL is not empty before setting
        if (!url || url.trim() === '') {
          throw new Error('Playback URL is empty or not available');
        }

        // Clear pending/processing state
        setIsPending(false);
        setIsProcessing(false);
        setPendingReason(null);
        setProcessingReason(null);
        setPlaybackUrl(url);
        return true; // Ready
      } else {
        console.error('Failed to fetch playback URL:', response.error);
        setError(response.error?.message || 'Failed to load video');
        setErrorType('auth');
        setIsLoading(false);
        return false;
      }
    } catch (err) {
      console.error('Failed to fetch playback URL:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);

      // Check if this is a development mode error or missing Cloudflare credentials
      if (
        errorMessage.includes('development mode') ||
        errorMessage.includes('Cloudflare Stream credentials') ||
        errorMessage.includes('Cloudflare Stream API token not configured') ||
        errorMessage.includes('mock stream') ||
        errorMessage.includes('mock-playback.m3u8') ||
        errorMessage.includes('playback URL not available')
      ) {
        console.warn('Stream playback not available:', errorMessage);
        // Clear error to show placeholder instead of error message
        setError(null);
        setErrorType('unknown');
        setIsLoading(false);
        // Don't set a playback URL, let the component render the development placeholder
      } else {
        setError(errorMessage);
        setErrorType('playback');
        setIsLoading(false);
      }
      return false;
    }
  }, [stream.id]);

  // Fetch signed playback URL from API
  useEffect(() => {
    const initPlayback = async () => {
      setIsLoading(true);
      const isReady = await fetchPlaybackUrl();

      // If not ready, we'll wait for WebSocket status update to retry
      if (!isReady) {
        console.log(`Stream ${stream.id} playback URL not ready, waiting for status update...`);
      }
    };

    if (stream.id && stream.status !== 'scheduled') {
      initPlayback();
    } else {
      setIsLoading(false);
    }
  }, [stream.id, stream.status, fetchPlaybackUrl]);

  // Real-time WebSocket subscription for stream status updates
  // When status changes, check for playback URL availability
  const handleStreamStatusMessage = useCallback((message: any) => {
    if (message.type === 'stream_status_changed' && message.streamId === stream.id) {
      // Status changed - check if playback URL is now available
      if (message.status === 'live' || message.status === 'ended') {
        fetchPlaybackUrl().then((isReady: boolean) => {
          if (isReady) {
            console.log(`Stream ${stream.id} playback URL is now available`);
          }
        });
      }
    }
  }, [stream.id, fetchPlaybackUrl]);

  useDurableObjectChannel({
    channel: stream.id ? `stream:status:${stream.id}` : '',
    enabled: !!stream.id && stream.status !== 'scheduled',
    onMessage: handleStreamStatusMessage,
  });

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    // Rule 1: Duration is only valid when metadata is FINAL
    // For recordings: set duration once and only if valid
    if (!isLiveStream) {
      if (video.readyState >= 1 && isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
      // If duration is not available, leave it as 0 (unknown)
    } else {
      // Rule 2: Live streams DO NOT have duration
      // Only track seekable range
      const seekable = getSeekableRange(video);
      const bufferDuration = seekable.end - seekable.start;
      if (bufferDuration > 0 && isFinite(bufferDuration)) {
        setSeekableStart(seekable.start);
        setSeekableEnd(seekable.end);
        setLiveBufferDuration(bufferDuration);
        // DO NOT set duration for live streams
      }
    }
    setIsLoading(false);
  }, [isLiveStream]);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isLiveStream) {
      // For live streams, track time relative to seekable start
      const seekable = getSeekableRange(video);
      const absoluteTime = video.currentTime;
      const relativeTime = absoluteTime - seekable.start;
      setCurrentTime(Math.max(0, relativeTime));

      // Update live buffer duration from seekable range
      const bufferDuration = seekable.end - seekable.start;
      if (bufferDuration > 0 && isFinite(bufferDuration)) {
        setSeekableStart(seekable.start);
        setSeekableEnd(seekable.end);
        setLiveBufferDuration(bufferDuration);
      }
    } else {
      // For recordings, use absolute time
      // NEVER update duration here - it's set once on loadedmetadata
      const currentVideoTime = video.currentTime || 0;
      setCurrentTime(currentVideoTime);
    }
  }, [isLiveStream]);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleWaiting = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleCanPlay = useCallback(() => {
    setIsLoading(false);
  }, []);

  const handleError = useCallback(() => {
    setError('Failed to load video');
    setIsLoading(false);
  }, []);

  useEffect(() => {
    if (useCloudflarePlayer) return;
    if (videoRef.current) {
      videoRef.current.muted = isAudioMuted;
    }
  }, [isAudioMuted, useCloudflarePlayer]);

  // Initialize HLS.js for HLS streams
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return;

    // Check if this is a development mode error
    if (playbackUrl.includes('development mode')) {
      console.warn('Development mode playback URL detected');
      setIsLoading(false);
      setError(playbackUrl);
      setErrorType('playback');
      return;
    }

    // Validate that we have a valid video URL format
    if (!playbackUrl.match(/^https?:\/\//) && !playbackUrl.startsWith('blob:')) {
      console.warn('Invalid playback URL format:', playbackUrl);
      setIsLoading(false);
      setError('Invalid video URL format. Please ensure the stream has a valid Cloudflare Stream video UID configured.');
      setErrorType('playback');
      return;
    }

    const isHls = playbackUrl.includes('.m3u8');
    const isCloudflare = isCloudflareStreamUrl(playbackUrl);

    // Cleanup previous instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (isHls) {
      // For Cloudflare Stream URLs, use native video playback instead of hls.js
      // This avoids CORS issues since Cloudflare Stream blocks XHR/fetch requests
      // but allows native browser HLS playback
      if (isCloudflare) {
        console.log('Using native playback for Cloudflare Stream URL');

        // All modern browsers support HLS natively now
        // Safari has had it forever, Chrome/Edge/Firefox added support ~2020
        video.src = playbackUrl;

        // Handle native playback errors - fallback to iframe if needed
        const handleNativeError = () => {
          console.warn('Native HLS playback failed, falling back to iframe player');
          // Remove error listener to prevent loops
          video.removeEventListener('error', handleNativeError);
          // Switch to iframe player
          setUseIframePlayer(true);
          setError(null);
          setIsLoading(false);
        };

        video.addEventListener('error', handleNativeError);

        // Handle successful load
        video.addEventListener('loadeddata', () => {
          video.removeEventListener('error', handleNativeError);
          setIsLoading(false);
          if (autoPlay) {
            video.play().catch(() => {
              setIsPlaying(false);
            });
          }
        }, { once: true });

        // Add event listeners for time tracking
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('error', handleError);

        setIsLoading(false);
      } else if (Hls.isSupported()) {
        // For non-Cloudflare HLS sources, use hls.js
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: stream.status === 'live',
          backBufferLength: stream.status === 'live' ? 30 : 90,
        });

        hls.loadSource(playbackUrl);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, (_, data) => {
          setIsLoading(false);

          // Get quality levels
          const levels = data.levels.map((level) => ({
            height: level.height,
            bitrate: level.bitrate,
            label: level.height ? `${level.height}p` : `${Math.round(level.bitrate / 1000)}kbps`
          }));
          setQualityLevels(levels);

          // Add event listeners for time tracking
          video.addEventListener('loadedmetadata', handleLoadedMetadata);
          video.addEventListener('timeupdate', handleTimeUpdate);
          video.addEventListener('ended', handleEnded);
          video.addEventListener('play', handlePlay);
          video.addEventListener('pause', handlePause);
          video.addEventListener('waiting', handleWaiting);
          video.addEventListener('canplay', handleCanPlay);
          video.addEventListener('error', handleError);

          if (autoPlay) {
            video.play().catch(() => {
              // Autoplay blocked, need user interaction
              setIsPlaying(false);
            });
          }
        });

        hls.on(Hls.Events.ERROR, (_, data) => {
          console.error('HLS Error:', data);

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                setError('Network connection lost. Retrying...');
                setErrorType('network');
                // Auto-retry with exponential backoff
                setTimeout(() => {
                  if (retryCount < 3) {
                    setRetryCount(prev => prev + 1);
                    hls.startLoad();
                  } else {
                    setError('Unable to connect after multiple attempts. Please check your internet connection.');
                  }
                }, Math.min(1000 * Math.pow(2, retryCount), 10000));
                break;
              case Hls.ErrorTypes.MEDIA_ERROR:
                setError('Video format error - attempting to recover...');
                setErrorType('playback');
                setIsRecovering(true);
                hls.recoverMediaError();
                setTimeout(() => setIsRecovering(false), 2000);
                break;
              default:
                setError('Playback error occurred. Please try refreshing the page.');
                setErrorType('playback');
                hls.destroy();
                break;
            }
          } else {
            // Non-fatal errors - just log and continue
            console.warn('Non-fatal HLS error:', data);
          }
        });

        hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
          setCurrentQuality(data.level);
        });

        hlsRef.current = hls;
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS support (Safari) for non-Cloudflare URLs
        video.src = playbackUrl;

        // Add event listeners for time tracking
        video.addEventListener('loadedmetadata', handleLoadedMetadata);
        video.addEventListener('timeupdate', handleTimeUpdate);
        video.addEventListener('ended', handleEnded);
        video.addEventListener('play', handlePlay);
        video.addEventListener('pause', handlePause);
        video.addEventListener('waiting', handleWaiting);
        video.addEventListener('canplay', handleCanPlay);
        video.addEventListener('error', handleError);

        setIsLoading(false);
      } else {
        setError('HLS is not supported in this browser');
        setIsLoading(false);
      }
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      // Remove event listeners
      if (video) {
        video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        video.removeEventListener('timeupdate', handleTimeUpdate);
        video.removeEventListener('ended', handleEnded);
        video.removeEventListener('play', handlePlay);
        video.removeEventListener('pause', handlePause);
        video.removeEventListener('waiting', handleWaiting);
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
      }
    };
  }, [playbackUrl, stream.status, autoPlay, handleLoadedMetadata, handleTimeUpdate, handleEnded, handlePlay, handlePause, handleWaiting, handleCanPlay, handleError]);

  // Live buffer tracking for live streams - update seekable range only
  useEffect(() => {
    if (!isLiveStream || !playbackUrl || useCloudflarePlayer) return;

    const updateLiveBuffer = () => {
      const video = videoRef.current;
      if (!video) return;

      const seekable = getSeekableRange(video);
      const bufferDuration = seekable.end - seekable.start;

      if (bufferDuration > 0 && isFinite(bufferDuration)) {
        setSeekableStart(seekable.start);
        setSeekableEnd(seekable.end);
        setLiveBufferDuration(bufferDuration);
        // NEVER set duration for live streams
      }
    };

    // Update every 1 second for live streams
    const interval = setInterval(updateLiveBuffer, 1000);
    updateLiveBuffer(); // Initial update

    return () => clearInterval(interval);
  }, [isLiveStream, playbackUrl, useCloudflarePlayer]);

  // NO duration polling - duration is set once on loadedmetadata only

  const [isRecovering, setIsRecovering] = useState(false);

  // Auto-hide controls state
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Auto-hide controls after 3 seconds of inactivity
  const resetControlsTimeout = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    setShowControls(true);
    lastActivityRef.current = Date.now();

    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  // Mouse movement handler for auto-hide
  const handleMouseMove = useCallback(() => {
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  // Keyboard interaction handler for auto-hide
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Ignore if user is typing in an input field
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }
    resetControlsTimeout();
  }, [resetControlsTimeout]);

  // Set up event listeners for auto-hide
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseenter', handleMouseMove);
    container.addEventListener('mouseleave', () => {
      if (isPlaying) {
        setShowControls(false);
      }
    });
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseenter', handleMouseMove);
      container.removeEventListener('mouseleave', resetControlsTimeout);
      document.removeEventListener('keydown', handleKeyDown);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [handleMouseMove, handleKeyDown, isPlaying, resetControlsTimeout]);

  // Picture-in-Picture state
  const [isPipActive, setIsPipActive] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);

  // Toggle Picture-in-Picture
  const togglePip = useCallback(async () => {
    const videoElement = videoRef.current;
    const cloudflarePlayer = cloudflareStreamRef.current;

    if (useCloudflarePlayer && cloudflarePlayer) {
      // Cloudflare iframe player supports PiP through the iframe API
      // The iframe already has picture-in-picture permission
      // We can try to request PiP on the iframe element
      const iframe = containerRef.current?.querySelector('iframe') as HTMLIFrameElement | null;
      if (iframe && 'requestPictureInPicture' in iframe && typeof iframe.requestPictureInPicture === 'function') {
        try {
          if (isPipActive) {
            if ('exitPictureInPicture' in iframe && typeof iframe.exitPictureInPicture === 'function') {
              await iframe.exitPictureInPicture();
            }
          } else {
            await iframe.requestPictureInPicture();
          }
          setIsPipActive(!isPipActive);
        } catch (error) {
          console.warn('Picture-in-Picture not supported or failed:', error);
        }
      }
    } else if (videoElement) {
      try {
        if (isPipActive) {
          if (document.pictureInPictureElement) {
            await document.exitPictureInPicture();
          }
        } else {
          if ('requestPictureInPicture' in videoElement && typeof videoElement.requestPictureInPicture === 'function') {
            await videoElement.requestPictureInPicture();
          }
        }
        setIsPipActive(!isPipActive);
      } catch (error) {
        console.warn('Picture-in-Picture not supported or failed:', error);
      }
    }
  }, [isPipActive, useCloudflarePlayer]);

  // Change playback speed
  const changePlaybackSpeed = useCallback((speed: number) => {
    const videoElement = videoRef.current;
    const cloudflarePlayer = cloudflareStreamRef.current;

    if (useCloudflarePlayer && cloudflarePlayer) {
      // Cloudflare player supports playbackRate
      const player = cloudflarePlayer as any;
      if (player.playbackRate !== undefined) {
        player.playbackRate = speed;
        setPlaybackSpeed(speed);
      }
    } else if (videoElement) {
      videoElement.playbackRate = speed;
      setPlaybackSpeed(speed);
    }
  }, [useCloudflarePlayer]);

  // Speed options
  const speedOptions = [1, 1.25, 1.5, 1.75, 2];

  // Keyboard shortcuts for speed control
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Speed control with Shift + [ and Shift + ]
      if (event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
        if (event.key === '[') {
          event.preventDefault();
          const currentIndex = speedOptions.indexOf(playbackSpeed);
          const newIndex = Math.max(0, currentIndex - 1);
          changePlaybackSpeed(speedOptions[newIndex]);
        } else if (event.key === ']') {
          event.preventDefault();
          const currentIndex = speedOptions.indexOf(playbackSpeed);
          const newIndex = Math.min(speedOptions.length - 1, currentIndex + 1);
          changePlaybackSpeed(speedOptions[newIndex]);
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [playbackSpeed, changePlaybackSpeed]);

  const togglePlay = useCallback(() => {
    // Cloudflare Stream: Use SDK methods if available, but they may not be reliable
    // Native video: Use imperative API (always reliable)

    if (useCloudflarePlayer && cloudflareStreamRef.current) {
      const player = cloudflareStreamRef.current;
      // Try to use SDK methods if they exist, but don't rely on them
      try {
        if (isPlaying) {
          if (player.pause && typeof player.pause === 'function') {
            player.pause();
          } else {
            // Fallback: SDK doesn't support pause, rely on event-driven state
            console.debug('Cloudflare player pause not available, relying on event-driven state');
          }
        } else {
          if (player.play && typeof player.play === 'function') {
            player.play().catch((err: any) => {
              console.debug('Cloudflare player play failed, relying on event-driven state:', err);
              setIsPlaying(false);
            });
          } else {
            // Fallback: SDK doesn't support play, rely on event-driven state
            console.debug('Cloudflare player play not available, relying on event-driven state');
          }
        }
      } catch (error) {
        // SDK methods failed, rely on event-driven state from onPlay/onPause
        console.debug('Cloudflare player control error, relying on event-driven state:', error);
      }
    } else if (videoRef.current && !useCloudflarePlayer) {
      // Native video element: imperative API is reliable
      try {
        if (isPlaying) {
          videoRef.current.pause();
        } else {
          videoRef.current.play().catch((err) => {
            console.error('Native video play failed:', err);
            setIsPlaying(false);
          });
        }
      } catch (error) {
        console.error('Error toggling play/pause on native video:', error);
      }
    }
  }, [isPlaying, useCloudflarePlayer]);

  const toggleMute = useCallback(() => {
    // Cloudflare Stream player handles its own mute controls internally
    if (videoRef.current && !useCloudflarePlayer) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  }, [isMuted, useCloudflarePlayer]);

  const handleVolumeChange = useCallback((value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);

    // Cloudflare Stream player handles its own volume controls internally
    if (videoRef.current && !useCloudflarePlayer) {
      videoRef.current.volume = newVolume;
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    }
  }, [useCloudflarePlayer, isMuted]);

  const handleSeek = useCallback((value: number[]) => {
    // StreamProgressBar now passes absolute time (seekStart + relative)
    const absoluteTime = value[0];

    if (isLiveStream) {
      // For live streams, seek within the buffer window
      if (videoRef.current && !useCloudflarePlayer) {
        const video = videoRef.current;
        // Ensure we're seeking within the seekable range
        const seekable = getSeekableRange(video);
        const clampedTime = Math.max(seekable.start, Math.min(seekable.end, absoluteTime));
        video.currentTime = clampedTime;
        // Update currentTime to relative time
        setCurrentTime(clampedTime - seekable.start);
      } else if (useCloudflarePlayer && cloudflareStreamRef.current) {
        // Use Cloudflare player API
        // Cloudflare player's currentTime is relative to buffer start (0-based)
        // Convert absolute time to relative time
        const relativeTime = Math.max(0, absoluteTime - seekableStart);
        const player = cloudflareStreamRef.current;
        if (player && typeof player.currentTime !== 'undefined') {
          // Cloudflare expects relative time for seeking
          player.currentTime = relativeTime;
        }
        setCurrentTime(relativeTime);
      }
    } else {
      // For recordings, absoluteTime is already absolute time
      if (videoRef.current && !useCloudflarePlayer) {
        const video = videoRef.current;
        video.currentTime = absoluteTime;
        setCurrentTime(absoluteTime);
      } else if (useCloudflarePlayer && cloudflareStreamRef.current) {
        const player = cloudflareStreamRef.current;
        if (player && typeof player.currentTime !== 'undefined') {
          player.currentTime = absoluteTime;
        }
        setCurrentTime(absoluteTime);
      }
    }
  }, [isLiveStream, seekableStart, useCloudflarePlayer]);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Cloudflare Stream player handles its own progress updates internally

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      // Ignore if user is typing in an input field
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      switch (event.code) {
        case 'Space':
          event.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (isLiveStream) {
            // For live streams, seek within buffer window
            // currentTime is relative, calculate new relative time
            const newRelativeTime = Math.max(0, currentTime - 10);
            const newAbsoluteTime = seekableStart + newRelativeTime;
            if (videoRef.current && !useCloudflarePlayer) {
              const video = videoRef.current;
              const seekable = getSeekableRange(video);
              const clampedTime = Math.max(seekable.start, Math.min(seekable.end, newAbsoluteTime));
              video.currentTime = clampedTime;
              setCurrentTime(clampedTime - seekable.start);
            } else if (useCloudflarePlayer && cloudflareStreamRef.current) {
              // For Cloudflare, currentTime is relative, so seek with relative time
              const player = cloudflareStreamRef.current;
              if (player && typeof player.currentTime !== 'undefined') {
                // Cloudflare player expects relative time for seeking
                player.currentTime = newRelativeTime;
              }
              setCurrentTime(newRelativeTime);
            }
          } else {
            // For recordings, use absolute time
            const newTime = Math.max(0, currentTime - 10);
            if (videoRef.current && !useCloudflarePlayer) {
              videoRef.current.currentTime = newTime;
              setCurrentTime(newTime);
            } else if (useCloudflarePlayer && cloudflareStreamRef.current) {
              const player = cloudflareStreamRef.current;
              if (player && typeof player.currentTime !== 'undefined') {
                player.currentTime = newTime;
              }
              setCurrentTime(newTime);
            }
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (isLiveStream) {
            // For live streams, seek within buffer window
            const maxRelativeTime = liveBufferDuration || duration;
            const newRelativeTime = Math.min(maxRelativeTime, currentTime + 10);
            const newAbsoluteTime = seekableStart + newRelativeTime;
            if (videoRef.current && !useCloudflarePlayer) {
              const video = videoRef.current;
              const seekable = getSeekableRange(video);
              const clampedTime = Math.max(seekable.start, Math.min(seekable.end, newAbsoluteTime));
              video.currentTime = clampedTime;
              setCurrentTime(clampedTime - seekable.start);
            } else if (useCloudflarePlayer && cloudflareStreamRef.current) {
              // For Cloudflare, currentTime is relative, so seek with relative time
              const player = cloudflareStreamRef.current;
              if (player && typeof player.currentTime !== 'undefined') {
                // Cloudflare player expects relative time for seeking
                player.currentTime = newRelativeTime;
              }
              setCurrentTime(newRelativeTime);
            }
          } else {
            // For recordings, use absolute time
            const newTime = Math.min(duration, currentTime + 10);
            if (videoRef.current && !useCloudflarePlayer) {
              videoRef.current.currentTime = newTime;
              setCurrentTime(newTime);
            } else if (useCloudflarePlayer && cloudflareStreamRef.current) {
              const player = cloudflareStreamRef.current;
              if (player && typeof player.currentTime !== 'undefined') {
                player.currentTime = newTime;
              }
              setCurrentTime(newTime);
            }
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [currentTime, duration, stream.status, useCloudflarePlayer, isLiveStream, seekableStart, liveBufferDuration, togglePlay]);

  const handleQualityChange = (levelIndex: number) => {
    if (hlsRef.current) {
      hlsRef.current.currentLevel = levelIndex;
      setCurrentQuality(levelIndex);
    }
  };

  const handleRestart = () => {
    const video = videoRef.current;
    if (video) {
      video.currentTime = 0;
      video.play();
    }
  };

  const formatTime = (time: number) => {
    // Handle Infinity, NaN, and negative values
    if (!isFinite(time) || time < 0) return '0:00';

    const h = Math.floor(time / 3600);
    const m = Math.floor((time % 3600) / 60);
    const s = Math.floor(time % 60);

    return h > 0
      ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      : `${m}:${s.toString().padStart(2, '0')}`;
  };

  const iframeUrl = playbackUrl ? getIframeUrlFromManifest(playbackUrl) : null;

  return (
    <div
      ref={containerRef}
      className="relative bg-black rounded-lg overflow-hidden group"
      onMouseMove={handleMouseMove}
      onMouseEnter={handleMouseMove}
    >
      {/* Cloudflare Stream React Player (Primary for Cloudflare URLs) */}
      {/* When using Cloudflare player, it handles its own loading states - no custom overlays needed */}
      {useCloudflarePlayer && playbackUrl && isCloudflareStreamUrl(playbackUrl) && getVideoUidFromUrl(playbackUrl) ? (
        <div className="w-full aspect-video relative">
          <CloudflareStream
            src={getVideoUidFromUrl(playbackUrl)!}
            streamRef={cloudflareStreamRef}
            autoplay={autoPlay}
            muted={isAudioMuted}
            controls={false}
            preload="auto"
            poster={resolveBundledMedia(stream.thumbnailUrl) ?? stream.thumbnailUrl ?? undefined}
            primaryColor="hsl(var(--primary))"
            className="w-full"
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onTimeUpdate={(e: any) => {
              const player = cloudflareStreamRef.current;
              if (!player) return;

              const playerCurrentTime = (player as any).currentTime || 0;

              if (isLiveStream) {
                // For live streams, track time relative to buffer start
                // Cloudflare Stream player's currentTime is already relative
                setCurrentTime(Math.max(0, playerCurrentTime));

                // Update live buffer from seekable range if available
                const playerDuration = (player as any).duration;
                if (playerDuration && isFinite(playerDuration) && playerDuration > 0) {
                  setLiveBufferDuration(playerDuration);
                  // NEVER set duration for live streams
                }
              } else {
                // For recordings, use absolute time
                // NEVER update duration here - it's set once on loadedmetadata
                setCurrentTime(playerCurrentTime);
              }
            }}
            onDurationChange={(e: Event) => {
              // Extract duration from event or player
              const player = cloudflareStreamRef.current;
              let rawDuration = 0;

              if (player && typeof (player as any).duration !== 'undefined') {
                rawDuration = (player as any).duration;
              } else if (e && 'detail' in e && typeof (e as any).detail === 'number') {
                rawDuration = (e as any).detail;
              } else if (e && 'target' in e && e.target) {
                const target = e.target as HTMLVideoElement;
                rawDuration = target.duration || 0;
              }

              // Rule 1: Duration is only valid when metadata is FINAL
              if (!isFinite(rawDuration) || rawDuration <= 0) {
                return; // Unknown duration, leave as 0
              }

              if (isLiveStream) {
                // Rule 2: Live streams DO NOT have duration
                // This is the buffer duration, not duration
                setLiveBufferDuration(rawDuration);
                // NEVER set duration for live streams
              } else {
                // For recordings, set duration once (only if valid)
                // This should only fire once on loadedmetadata, but we validate anyway
                setDuration(rawDuration);
              }
            }}
            onError={(error) => {
              const videoUid = getVideoUidFromUrl(playbackUrl);

              // Extract error details from CustomEvent or Error object
              let errorDetails: any = {};
              let errorMessage = '';

              if (error instanceof Error) {
                errorDetails = {
                  message: error.message,
                  name: error.name,
                  stack: error.stack,
                };
                errorMessage = error.message;
              } else if (error && typeof error === 'object') {
                // Handle CustomEvent or other event objects
                const event = error as any;
                errorDetails = {
                  type: event.type,
                  detail: event.detail,
                  message: event.message || event.detail?.message || String(error),
                  target: event.target,
                  currentTarget: event.currentTarget,
                };
                // Try to extract message from various places
                errorMessage = event.message ||
                  event.detail?.message ||
                  event.detail?.error?.message ||
                  (event.detail && String(event.detail)) ||
                  String(error);
              } else {
                errorMessage = String(error);
                errorDetails = { message: errorMessage };
              }

              // Check if this is just an analytics SDK error (can be ignored)
              // Analytics errors typically come from platform.dash.cloudflare.com or are network errors
              // that don't affect playback
              const isAnalyticsError =
                errorMessage.includes('platform.dash.cloudflare.com') ||
                errorMessage.includes('Failed to fetch') ||
                (errorDetails.detail && String(errorDetails.detail).includes('platform.dash')) ||
                (errorDetails.stack && errorDetails.stack.includes('platform.dash'));

              if (!isAnalyticsError) {
                console.warn('Cloudflare Stream player error, falling back to native player', {
                  videoUid,
                  playbackUrl,
                  streamId: stream.id,
                  streamStatus: stream.status,
                  errorDetails,
                  errorMessage,
                });
                setUseCloudflarePlayer(false);
                setIsLoading(true);
              } else {
                // Just log analytics errors without falling back (these are non-critical)
                console.debug('Cloudflare Stream analytics SDK error (non-critical, ignoring):', {
                  errorMessage,
                  errorType: errorDetails.type,
                });
              }
            }}
          />
        </div>
      ) : useIframePlayer && iframeUrl ? (
        /* Cloudflare Iframe Player (Fallback) */
        <div className="w-full aspect-video relative">
          <iframe
            src={iframeUrl}
            className="w-full h-full border-0"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            title={`${stream.title} video player`}
          />
          {/* Button to switch back to native player */}
          <div className="absolute top-2 right-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setUseIframePlayer(false);
                setUseCloudflarePlayer(true);
                setIsLoading(true);
              }}
              className="opacity-70 hover:opacity-100 text-xs"
            >
              Try Native Player
            </Button>
          </div>
        </div>
      ) : (
        /* Native Video Element */
        <video
          ref={videoRef}
          className="w-full aspect-video"
          poster={resolveBundledMedia(stream.thumbnailUrl) ?? stream.thumbnailUrl ?? undefined}
          muted={isAudioMuted}
          preload="metadata"
          playsInline
          aria-label={`${stream.title} video player`}
          role="application"
          aria-describedby="video-controls"
        >
          Your browser does not support the video tag.
        </video>
      )}

      {/* Stream Overlays */}
      <StreamOverlays
        hasAccess={hasAccess}
        streamId={stream.id}
        streamTitle={stream.title}
        streamStatus={stream.status}
        sourceType={stream.sourceType}
        scheduledStart={stream.scheduledStart}
        creator={stream.creator}
        tags={stream.tags}
        duration={stream.duration}
        viewerCount={stream.viewerCount}
        likes={stream.likes}
        isLoading={isLoading}
        error={error}
        errorType={errorType}
        isPending={isPending}
        pendingReason={pendingReason}
        isProcessing={isProcessing}
        processingReason={processingReason}
        playbackUrl={playbackUrl || ''}
      />

      {/* Live Caption Overlay — language selector + subtitle display */}
      <CaptionOverlay
        streamId={stream.id}
        isLive={isLiveStream}
        captionsActive={isLiveStream || isRecording}
        volume={volume}
        isMuted={isMuted}
        playbackTimeSec={currentTime}
        isPlaying={isPlaying}
        onDubStateChange={setIsDubAudioMutingOriginal}
      />

      {/* Controls Overlay - Auto-hide with 3s timeout */}
      <div
        id="video-controls"
        className={`
          absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm p-4
          transition-all duration-300 ease-in-out
          ${showControls ? 'opacity-100' : 'opacity-0'}
          ${isPlaying && !showControls ? 'pointer-events-none' : 'pointer-events-auto'}
        `}
        role="toolbar"
        aria-label="Video controls"
      >
        {/* Progress Bar - Contract: duration for recordings, liveBufferDuration for live */}
        {(() => {
          // Stream type contract:
          // Recording: use duration (set once on loadedmetadata)
          // Live: use liveBufferDuration (from seekable range)
          const sliderMax = isLiveStream ? liveBufferDuration : duration;

          return sliderMax > 0 && (
            <StreamProgressBar
              currentTime={currentTime}
              duration={sliderMax}
              streamStatus={stream.status}
              onSeek={handleSeek}
              formatTime={formatTime}
              isLiveStream={isLiveStream}
              liveBufferDuration={liveBufferDuration}
              // For Cloudflare player, coordinate system is relative (starts at 0)
              // For native video, use actual seekableStart
              seekableStart={useCloudflarePlayer ? 0 : seekableStart}
              chapters={stream.chapters}
              isRecording={!isLiveStream}
            />
          );
        })()}

        <StreamControls
          isPlaying={isPlaying}
          isMuted={isMuted}
          volume={volume}
          streamStatus={stream.status}
          qualityLevels={qualityLevels}
          currentQuality={currentQuality}
          isMobile={isMobile}
          onPlayPause={togglePlay}
          onMuteToggle={toggleMute}
          onVolumeChange={handleVolumeChange}
          onRestart={handleRestart}
          onQualityChange={handleQualityChange}
          onShowInfo={() => setShowInfoSheet(true)}
          onShowShortcuts={() => setShowShortcuts(true)}
          streamId={stream.id}
          likesCount={stream.likes || 0}
          userId={user?.id || 0}
          onTogglePip={togglePip}
          isPipActive={isPipActive}
          playbackSpeed={playbackSpeed}
          onSpeedChange={changePlaybackSpeed}
        />
      </div>
    </div>
  );
}
